import { z } from 'zod';

/** Tenant-level roles carried in the JWT `tenant_role` claim. */
export const TENANT_ROLES = ['owner', 'admin', 'member', 'guest'] as const;
export const TenantRoleSchema = z.enum(TENANT_ROLES);
export type TenantRole = z.infer<typeof TenantRoleSchema>;

/**
 * Closed set of permission keys. Permission schemes grant these to grantees per project.
 * Adding a key here is a contract change: update docs/domain and the admin UI matrix.
 */
export const PERMISSION_KEYS = [
  'project.view',
  'project.admin',
  'ticket.view_all',
  'ticket.view_own',
  'ticket.view_org',
  'ticket.create',
  'ticket.edit',
  'ticket.edit_own',
  'ticket.delete',
  'ticket.assign',
  'ticket.transition',
  'ticket.link',
  'ticket.move',
  'ticket.export',
  'comment.create',
  'comment.internal',
  'comment.edit_any',
  'attachment.create',
  'watcher.manage',
  'view.manage_shared',
  'approval.decide',
] as const;
export const PermissionKeySchema = z.enum(PERMISSION_KEYS);
export type PermissionKey = z.infer<typeof PermissionKeySchema>;

/** Who a permission grant applies to. Dynamic grantees are resolved against the ticket. */
export const GRANTEE_TYPES = [
  'project_role',
  'tenant_role',
  'user',
  'group',
  'reporter',
  'assignee',
  'project_lead',
  'anyone',
] as const;
export const GranteeTypeSchema = z.enum(GRANTEE_TYPES);
export type GranteeType = z.infer<typeof GranteeTypeSchema>;

export const PermissionGrantSchema = z.object({
  permission: PermissionKeySchema,
  granteeType: GranteeTypeSchema,
  /** Required for project_role, tenant_role (holds the role name), user and group. */
  granteeId: z.string().nullable(),
});
export type PermissionGrant = z.infer<typeof PermissionGrantSchema>;

/** System-seeded project roles; tenants may add their own. */
export const SYSTEM_PROJECT_ROLES = ['project_admin', 'agent', 'customer'] as const;
